import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AppointmentModal from "@/components/calendar/AppointmentModal";
import DayView from "@/components/calendar/DayView";
import WeekView from "@/components/calendar/WeekView";
import MonthView from "@/components/calendar/MonthView";
import { endpoints } from "@/lib/api";

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = x.getDay(); x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const fmtMonth = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
const fmtLong = (d) => d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function CalendarPage() {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [consultantFilter, setConsultantFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, initial: null });

  const load = () => endpoints.listAppointments().then(setAppts).catch(() => {});
  useEffect(() => { load(); endpoints.listConsultants().then(setConsultants); }, []);

  const filtered = useMemo(() => (
    consultantFilter === "all" ? appts : appts.filter((a) => a.consultant === consultantFilter)
  ), [appts, consultantFilter]);

  const shift = (dir) => {
    const map = { day: 1, week: 7, month: 30 };
    if (view === "month") {
      const x = new Date(cursor); x.setMonth(x.getMonth() + dir); setCursor(x);
    } else {
      setCursor(addDays(cursor, dir * map[view]));
    }
  };

  const header = view === "month"
    ? fmtMonth(cursor)
    : view === "week"
      ? `${fmtLong(startOfWeek(cursor)).replace(/,.*/, "")} → ${fmtLong(addDays(startOfWeek(cursor), 6)).replace(/,.*/, "")}`
      : fmtLong(cursor);

  const openCreate = (prefill = {}) => setModal({ open: true, initial: { ...prefill } });
  const openEdit = (a) => setModal({ open: true, initial: a });

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-indigo-500/25">
            <CalendarDays className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Calendar & Diary</h1>
            <p className="text-xs text-slate-500">Schedule visits, audits and reviews</p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <div className="flex items-center gap-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-1">
            <Button data-testid="cal-prev-button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button data-testid="cal-today-button" variant="ghost" className="h-8 px-3 text-xs font-semibold" onClick={() => setCursor(new Date())}>
              Today
            </Button>
            <Button data-testid="cal-next-button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="cal-view-tabs" className="h-9 bg-slate-100 dark:bg-slate-800">
              <TabsTrigger value="day" className="text-xs h-7" data-testid="cal-view-day">Day</TabsTrigger>
              <TabsTrigger value="week" className="text-xs h-7" data-testid="cal-view-week">Week</TabsTrigger>
              <TabsTrigger value="month" className="text-xs h-7" data-testid="cal-view-month">Month</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="hidden md:flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <Select value={consultantFilter} onValueChange={setConsultantFilter}>
              <SelectTrigger data-testid="cal-consultant-filter" className="h-9 w-[180px] text-xs">
                <SelectValue placeholder="All consultants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All consultants</SelectItem>
                {consultants.map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button data-testid="cal-new-appointment-button" className="brand-gradient text-white h-9" onClick={() => openCreate({})}>
            <Plus className="h-4 w-4 mr-1" /> New appointment
          </Button>
        </div>
      </div>

      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-3 flex items-center justify-between">
        <h2 data-testid="cal-header-title" className="font-display text-lg sm:text-xl font-bold tracking-tight">{header}</h2>
        <Badge variant="secondary" className="rounded-full">{filtered.length} scheduled</Badge>
      </div>

      <div className="glass-card rounded-3xl p-2 sm:p-4">
        {view === "day" && <DayView date={cursor} appts={filtered} onOpen={openEdit} onCreate={openCreate} />}
        {view === "week" && <WeekView date={cursor} appts={filtered} onOpen={openEdit} onCreate={openCreate} />}
        {view === "month" && <MonthView date={cursor} appts={filtered} onOpen={openEdit} onCreate={openCreate} onJumpDay={(d) => { setCursor(d); setView("day"); }} />}
      </div>

      <AppointmentModal
        open={modal.open}
        initial={modal.initial}
        consultants={consultants}
        onOpenChange={(o) => setModal({ open: o, initial: modal.initial })}
        onChanged={() => { load(); setModal({ open: false, initial: null }); }}
      />
    </div>
  );
}
